import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { Layout } from './components/Layout/Layout';
//import { AsistenteIA } from 'pages/AsistenteIA';
import { m } from 'framer-motion';

const AprobarJustificaciones = React.lazy(() =>
  import('./pages/AprobarJustificaciones').then((m) => ({
    default: m.AprobarJustificaciones,
  })),
);
const CoberturaPuestoPage = React.lazy(
  () => import('./pages/CoberturaPuestoPage'),
);
const NominaDetalle = React.lazy(() =>
  import('./pages/NominaDetalle').then((m) => ({ default: m.NominaDetalle })),
);
const Login = React.lazy(() =>
  import('./pages/Login').then((m) => ({ default: m.Login })),
);
const Dashboard = React.lazy(() =>
  import('./pages/Dashboard').then((m) => ({ default: m.Dashboard })),
);
const AsistenteIA = React.lazy(() =>
  import('./pages/AsistenteIA').then((m) => ({ default: m.AsistenteIA })),
);
const Empresas = React.lazy(() =>
  import('./pages/Empresas').then((m) => ({ default: m.Empresas })),
);
const Empleados = React.lazy(() =>
  import('./pages/Empleados').then((m) => ({ default: m.Empleados })),
);
const Asistencias = React.lazy(() =>
  import('./pages/Asistencias').then((m) => ({ default: m.Asistencias })),
);
const Nomina = React.lazy(() =>
  import('./pages/Nomina').then((m) => ({ default: m.Nomina })),
);
const Reglas = React.lazy(() =>
  import('./pages/Reglas').then((m) => ({ default: m.Reglas })),
);
const Reportes = React.lazy(() =>
  import('./pages/Reportes').then((m) => ({ default: m.Reportes })),
);
const Configuracion = React.lazy(() =>
  import('./pages/Configuracion').then((m) => ({ default: m.Configuracion })),
);
const Cargos = React.lazy(() =>
  import('./pages/Cargos').then((m) => ({ default: m.Cargos })),
);
const Departamentos = React.lazy(() =>
  import('./pages/Departamentos').then((m) => ({ default: m.Departamentos })),
);
const Adelantos = React.lazy(() =>
  import('./pages/Adelantos').then((m) => ({ default: m.Adelantos })),
);
const Prestamos = React.lazy(() =>
  import('./pages/Prestamos').then((m) => ({ default: m.Prestamos })),
);
const Sanciones = React.lazy(() =>
  import('./pages/Sanciones').then((m) => ({ default: m.Sanciones })),
);
const Premios = React.lazy(() =>
  import('./pages/Premios').then((m) => ({ default: m.Premios })),
);
const Usuarios = React.lazy(() =>
  import('./pages/Usuarios').then((m) => ({ default: m.Usuarios })),
);
const Horarios = React.lazy(() =>
  import('./pages/Horarios').then((m) => ({ default: m.Horarios })),
);
const Turnos = React.lazy(() =>
  import('./pages/Turnos').then((m) => ({ default: m.Turnos })),
);
const Rotaciones = React.lazy(() => import('./pages/Rotaciones'));
const PlanificadorGuardias = React.lazy(
  () => import('./pages/PlanificadorGuardias'),
);
const Calendarios = React.lazy(() =>
  import('./pages/Calendarios').then((m) => ({ default: m.Calendarios })),
);
const Perfiles = React.lazy(() =>
  import('./pages/Perfiles').then((m) => ({ default: m.Perfiles })),
);
const Dispositivos = React.lazy(() =>
  import('./pages/Dispositivos').then((m) => ({ default: m.Dispositivos })),
);
const ActividadEmpleado = React.lazy(() =>
  import('./pages/ActividadEmpleado').then((m) => ({
    default: m.ActividadEmpleado,
  })),
);
const Ayuda = React.lazy(() =>
  import('./pages/Ayuda').then((m) => ({ default: m.Ayuda })),
);
const CargaMasivaAsistencia = React.lazy(() =>
  import('./pages/CargaMasivaAsistencia').then((m) => ({
    default: m.CargaMasivaAsistencia,
  })),
);
const TiposContrato = React.lazy(() =>
  import('./pages/TiposContrato').then((m) => ({ default: m.TiposContrato })),
);

const LoadingFallback: React.FC = () => (
  <div className="min-h-screen bg-dark-bg flex items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent"></div>
  </div>
);

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-dark-bg flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <Layout>
                <Routes>
                  <Route
                    path="/"
                    element={<Navigate to="/dashboard" replace />}
                  />
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/AsistenteIA" element={<AsistenteIA />} />
                  <Route path="/empresas" element={<Empresas />} />
                  <Route path="/usuarios" element={<Usuarios />} />
                  <Route path="/empleados" element={<Empleados />} />
                  <Route path="/asistencias" element={<Asistencias />} />
                  <Route path="/nomina" element={<Nomina />} />
                  <Route path="/nomina/:id" element={<NominaDetalle />} />
                  <Route path="/reglas" element={<Reglas />} />
                  <Route path="/cargos" element={<Cargos />} />
                  <Route path="/departamentos" element={<Departamentos />} />
                  <Route path="/reportes" element={<Reportes />} />
                  <Route path="/adelantos" element={<Adelantos />} />
                  <Route path="/prestamos" element={<Prestamos />} />
                  <Route path="/sanciones" element={<Sanciones />} />
                  <Route path="/premios" element={<Premios />} />
                  <Route path="/configuracion" element={<Configuracion />} />
                  <Route path="/horarios" element={<Horarios />} />
                  <Route path="/turnos" element={<Turnos />} />
                  <Route path="/rotaciones" element={<Rotaciones />} />
                  <Route
                    path="/planificador"
                    element={<PlanificadorGuardias />}
                  />
                  <Route path="/calendarios" element={<Calendarios />} />
                  <Route path="/perfiles" element={<Perfiles />} />
                  <Route path="/tipos-contrato" element={<TiposContrato />} />
                  <Route path="/dispositivos" element={<Dispositivos />} />
                  <Route path="/actividad" element={<ActividadEmpleado />} />
                  <Route path="/cobertura" element={<CoberturaPuestoPage />} />
                  <Route
                    path="/asistencias/carga-masiva"
                    element={<CargaMasivaAsistencia />}
                  />
                  <Route path="/ayuda" element={<Ayuda />} />
                  <Route
                    path="/tipos-dia"
                    element={React.createElement(
                      React.lazy(() => import('./pages/TiposDia')),
                    )}
                  />
                  <Route
                    path="/aprobar-justificaciones"
                    element={<AprobarJustificaciones />}
                  />
                </Routes>
              </Layout>
            </ProtectedRoute>
          }
        />
      </Routes>
    </Suspense>
  );
};

export default App;
